// Central registry of every Brand OS navigation route grouped by Roger's
// workflow (Today -> Plan -> Create -> Publish -> Results -> Audience ->
// Brand Vault), with Admin and Settings tucked under the account menu.
//
// Every route listed here is a real route defined in `App.jsx`. Pre-login
// routes (login/magic-link/auth callback) and destination-only routes
// (channels/connect-facebook, review-walkthrough, NotFound) are not shown
// in the chrome but remain reachable by direct URL.
//
// `end: true` on an item means the active match is exact (used for the
// Command Center index route so it doesn't swallow every /brand-os/*).

export const NAV_GROUPS = [
  {
    key: "today",
    label: "Today",
    testId: "nav-group-today",
    items: [
      { label: "Home", to: "/brand-os", end: true, testId: "nav-command-center" },
      { label: "Founder Inbox", to: "/brand-os/inbox", testId: "nav-inbox" },
      { label: "Social Inbox", to: "/brand-os/social-inbox", testId: "nav-social-inbox" },
      { label: "Autonomy", to: "/brand-os/autonomy", testId: "nav-autonomy" },
      { label: "Approvals", to: "/brand-os/review-queue", testId: "nav-review-queue" },
      { label: "Conflicts", to: "/brand-os/conflicts", testId: "nav-conflicts" },
    ],
  },
  {
    key: "plan",
    label: "Plan",
    testId: "nav-group-plan",
    items: [
      { label: "Campaigns", to: "/brand-os/campaigns", testId: "nav-campaigns" },
      { label: "Planner", to: "/brand-os/planner", testId: "nav-planner" },
      // P4 — renamed from "Campaign Walkthrough" because /golden-path
      // actually renders the Genesis end-to-end test harness, not a
      // customer-facing campaign tour. Keeping it in Plan so power
      // users can still reach it; the label now matches the content.
      { label: "Genesis Test Harness", to: "/brand-os/golden-path", testId: "nav-golden-path" },
      { label: "Brand Interview", to: "/brand-os/interview", testId: "nav-interview" },
    ],
  },
  {
    key: "create",
    label: "Create",
    testId: "nav-group-create",
    items: [
      { label: "Create Overview", to: "/brand-os/create", testId: "nav-create-overview" },
      // 2026-10-26 — legacy "Creative Studio" nav item removed; it
      // pointed to the deleted copywriter page. All copy now goes
      // through the governed Creative tab on each campaign.
      { label: "Campaigns", to: "/brand-os/campaigns", testId: "nav-campaigns-create" },
      { label: "Asset Library", to: "/brand-os/library", testId: "nav-library" },
      { label: "Founder Videos", to: "/brand-os/videos", testId: "nav-videos" },
      { label: "Import video", to: "/brand-os/create/import-video", testId: "nav-import-video" },
      { label: "Ad Creation Wizard", to: "/brand-os/ads/create", testId: "nav-ads-create" },
    ],
  },
  {
    key: "publish",
    label: "Publish",
    testId: "nav-group-publish",
    items: [
      { label: "New Publish", to: "/brand-os/publish/new", testId: "nav-publish-new" },
      { label: "Publish Queue", to: "/brand-os/publish/queue", testId: "nav-publish-queue" },
      { label: "Post to TikTok", to: "/brand-os/publish/tiktok", testId: "nav-publish-tiktok" },
      { label: "Publish Calendar", to: "/brand-os/publish/calendar", testId: "nav-publish-calendar" },
      { label: "Publishing Overview", to: "/brand-os/operate", testId: "nav-operate-overview" },
      { label: "Channel Connections", to: "/brand-os/admin/channel-connections", testId: "nav-channel-connections" },
    ],
  },
  {
    key: "results",
    label: "Results",
    testId: "nav-group-results",
    items: [
      { label: "Insights Overview", to: "/brand-os/insights", testId: "nav-insights" },
      { label: "Channel Insights", to: "/brand-os/channel-insights", testId: "nav-channel-insights" },
      { label: "TikTok Results", to: "/brand-os/publish/tiktok/results", testId: "nav-tiktok-results" },
      { label: "Learning Loop", to: "/brand-os/learning", testId: "nav-learning" },
      { label: "Ads Insights", to: "/brand-os/ads/insights", testId: "nav-ads-insights" },
      { label: "Claims vs. Results", to: "/brand-os/truth-gap", testId: "nav-truth-gap" },
    ],
  },
  {
    key: "audience",
    label: "Audience",
    testId: "nav-group-audience",
    items: [
      { label: "Founding Waitlist", to: "/brand-os/waitlist", end: true, testId: "nav-waitlist" },
      { label: "Email Preview", to: "/brand-os/waitlist/email-preview", testId: "nav-email-preview" },
      { label: "Audiences", to: "/brand-os/audiences", testId: "nav-audiences" },
      { label: "Audience Notes", to: "/brand-os/audience-brain", testId: "nav-audience-brain" },
      { label: "Ad Accounts (Lead Ads)", to: "/brand-os/ads", end: true, testId: "nav-ads" },
    ],
  },
  {
    key: "vault",
    label: "Vault",
    testId: "nav-group-vault",
    items: [
      { label: "Overview", to: "/brand-os/foundation", testId: "nav-foundation-overview" },
      { label: "Brand Vault", to: "/brand-os/vault", end: true, testId: "nav-vault" },
      { label: "Guardian Vault (edit)", to: "/brand-os/vault/guardian", testId: "nav-vault-guardian" },
      { label: "Brand Notes", to: "/brand-os/brand-brain", testId: "nav-brand-brain" },
      { label: "Products", to: "/brand-os/products", testId: "nav-products" },
      { label: "Product Notes", to: "/brand-os/product-brain", testId: "nav-product-brain" },
      { label: "Knowledge Overview", to: "/brand-os/knowledge", testId: "nav-knowledge-overview" },
    ],
  },
];

// Admin lives inside the account menu on the right — same shape so we can
// render it with the same dropdown component.
export const ADMIN_GROUP = {
  key: "admin",
  label: "Admin",
  testId: "nav-group-admin",
  items: [
    { label: "Governance Log", to: "/brand-os/governance", testId: "nav-governance" },
    { label: "Search Index (admin)", to: "/brand-os/retrieval", testId: "nav-retrieval" },
    // Settings is currently disabled (no route behind it) — kept so the
    // chrome matches Shell's pre-redesign list and nothing is dropped.
    { label: "Settings", to: "/brand-os/settings", disabled: true, testId: "nav-settings" },
  ],
};

export function flattenNav() {
  const out = [];
  for (const g of NAV_GROUPS) {
    for (const it of g.items) out.push({ ...it, group: g.label });
  }
  for (const it of ADMIN_GROUP.items) out.push({ ...it, group: ADMIN_GROUP.label });
  return out;
}

export function groupFromPathname(pathname) {
  // Return the group key whose item best matches the pathname — longest
  // match wins so /brand-os/publish/new beats /brand-os/publish.
  let bestKey = null;
  let bestLen = -1;
  for (const g of NAV_GROUPS) {
    for (const it of g.items) {
      const to = it.to;
      if (!to) continue;
      const exact = pathname === to;
      const prefix = pathname.startsWith(to + "/");
      if ((exact || prefix) && to.length > bestLen) {
        bestKey = g.key;
        bestLen = to.length;
      }
    }
  }
  for (const it of ADMIN_GROUP.items) {
    const to = it.to;
    if (!to) continue;
    const exact = pathname === to;
    const prefix = pathname.startsWith(to + "/");
    if ((exact || prefix) && to.length > bestLen) {
      bestKey = ADMIN_GROUP.key;
      bestLen = to.length;
    }
  }
  return bestKey;
}
